import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Badge,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    ArrowBackIosNew,
    ArrowForwardIos,
    Close,
    Delete,
    Home as HomeIcon,
    Launch,
    Menu as MenuIcon,
    QuestionMark,
    RestartAlt,
    ShoppingCart,
    Summarize,
    ZoomIn,
    ZoomOut,
    SupportAgent,
    ExpandMore,
    ChevronRight,
    VerticalAlignTop,
} from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { FiChevronsDown, FiChevronsUp } from "react-icons/fi";

import PageTitle from "../../components/PageTitle/PageTitle";
import LoadingBoxes from "../../components/LoadingBoxes/LoadingBoxes";
import ContactCCRForm from "../../components/ContactCCRForm/ContactCCRForm";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import { useSite } from "../../context/siteContext";
import { useAuth } from "../../context/authContext";
import { useOptions } from "../../context/optionContext";
import { isRsplFeatureEnabled } from "../../utilities/rspl";
import { getMachineByNumberOrId } from "../../services/myMachineService";
import {
    getProductsByMachineId,
    normalizeMachineProductsResponse,
} from "../../services/machineCardService";
import { process2DModelFile } from "../../services/modelService";
import { addModelCartItems } from "../../services/cartService";
import { ASSETS_BASE_URL, RSPL_LIST, SERVER_BASE_URL } from "../../config";
import {
    buildRsplCodeToLabelMap,
    getRsplLabelForCode,
    parseRsplList,
} from "../../utilities/rspl";
import {
    PartCard,
    PartMenu,
    SearchInput,
    TabPanel,
} from "../ModelViewer/ModelViewer";
import {
    MACHINE_NODE_PREFIX,
    indexBomTree,
    collectAncestorIds,
} from "../../utilities/bomUtils";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const DEFAULT_ZOOM = 1;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.15;

const buildProductImageUrl = (product = {}) => {
    const raw = product?.asset_data?.images?.[0];
    if (raw) {
        const normalized = String(raw).trim();
        if (
            /^(https?:)?\/\//i.test(normalized) ||
            normalized.startsWith("data:")
        ) {
            return normalized;
        }
        const path = normalized.startsWith("/") ? normalized : `/${normalized}`;
        return `${ASSETS_BASE_URL}${path}`;
    }
    return `${ASSETS_BASE_URL}/ProductImages/${product?.product_number}.png`;
};

const filterTreeByTerm = (nodes = [], term = "") => {
    if (!term) return nodes;
    const normalized = term.trim().toLowerCase();

    const traverse = (items = []) => {
        const filtered = [];
        items.forEach((node) => {
            if (!node) return;
            const searchableFields = [
                node.productName,
                node.productNumber,
                node.product_number,
                node.productRef,
                node.nodeId,
                node.product_name,
                node.child_key,
            ]
                .filter(Boolean)
                .map((value) => String(value).toLowerCase());
            const matches = searchableFields.some((value) =>
                value.includes(normalized)
            );
            const childMatches = traverse(node.children || []);
            if (matches || childMatches.length > 0) {
                filtered.push({
                    ...node,
                    children: childMatches,
                });
            }
        });
        return filtered;
    };

    return traverse(nodes);
};

const normalizeLookupKey = (value) =>
    value ? String(value).trim().toLowerCase() : "";
const normalizeRsplType = (value) =>
    value ? String(value).trim().toLowerCase() : "";

const buildLookupVariants = (value) => {
    const base = normalizeLookupKey(value);
    if (!base) return [];
    const variants = new Set([base]);
    if (base.endsWith("_cp")) {
        variants.add(base.replace(/_cp$/i, ""));
    }
    if (base.includes(":")) {
        variants.add(base.split(":")[0]);
    }
    return Array.from(variants).filter(Boolean);
};

const extractFilenameFromPath = (source = "") => {
    if (!source) return "";
    const sanitized = String(source).split("?")[0];
    const parts = sanitized.split("/");
    return parts.length ? parts[parts.length - 1] : sanitized;
};

const formatPdfLabel = (candidate, sourcePath) => {
    const sourceName = extractFilenameFromPath(sourcePath);
    if (sourceName) return sourceName;
    if (candidate) return String(candidate);
    return "Drawing";
};

const PDFModelViewer = () => {
    const { machine_id, fileName: fileParam } = useParams();
    const location = useLocation();
    const decodedFileName = useMemo(
        () => (fileParam ? decodeURIComponent(fileParam) : ""),
        [fileParam]
    );
    const initialFileRef = useRef(decodedFileName);
    const pdfDragStartRef = useRef({
        mouseX: 0,
        mouseY: 0,
        panX: 0,
        panY: 0,
    });
    const zoomWheelFrameRef = useRef(null);
    const pendingWheelDirectionRef = useRef(0);
    const partMenuDragAnchorRef = useRef({
        mouseX: 0,
        mouseY: 0,
        dx: 0,
        dy: 0,
    });
    const partsTreeScrollRef = useRef(null);
    const subAssembliesScrollRef = useRef(null);
    const rsplScrollRef = useRef(null);
    const navigate = useNavigate();
    const returnPath = useMemo(() => {
        const fromPath = location?.state?.fromPath;
        if (typeof fromPath === "string" && fromPath.trim()) return fromPath;
        return "/machines";
    }, [location?.state?.fromPath]);
    const { t } = useTranslation();

    const loadingMessages = [
        t('GENERAL.LOADING_2D_DRAWING'),
        "Loading Products and Machine Data...",
        t('GENERAL.BUILDING_PARTS_TREE'),
        t('GENERAL.DRAWINGS_READY'),
    ];

    const { selectedSite, selectedSiteData, getSitePrice } = useSite();
    const { user } = useAuth();
    const { optionKeys } = useOptions();
    const rsplFeatureEnabled = isRsplFeatureEnabled(optionKeys);
    const showMenuTabSubtitles = optionKeys?.modelViewerShowTabSubtitles !== "false";
    const menuTabLabels = useMemo(
        () => ({
            subAssemblies: optionKeys?.modelViewerTabTitleSubAssemblies || "Sub Assemblies",
            rspl: optionKeys?.modelViewerTabTitleRspl || "RSPL Parts",
            partsTree: optionKeys?.modelViewerTabTitlePartsTree || "BOM",
        }),
        [
            optionKeys?.modelViewerTabTitleSubAssemblies,
            optionKeys?.modelViewerTabTitleRspl,
            optionKeys?.modelViewerTabTitlePartsTree,
        ]
    );

    const resolvedRsplList = useMemo(
        () => parseRsplList(optionKeys?.rsplList, RSPL_LIST),
        [optionKeys?.rsplList]
    );
    const rsplMapping = useMemo(
        () => buildRsplCodeToLabelMap(resolvedRsplList),
        [resolvedRsplList]
    );
    const rsplTypeLabels = useMemo(() => {
        const labels = (resolvedRsplList || [])
            .map((item) => String(item?.value || "").trim())
            .filter(Boolean);
        return Array.from(new Set(labels));
    }, [resolvedRsplList]);

    const [machine, setMachine] = useState(null);
    const [machineName, setMachineName] = useState("");
    const [machineNumber, setMachineNumber] = useState("");

    const [products, setProducts] = useState([]);
    const [productTypes, setProductTypes] = useState([]);
    const [productsByType, setProductsByType] = useState({});
    const [defaultPdfCandidate, setDefaultPdfCandidate] = useState(null);
    const [pendingTreeSelection, setPendingTreeSelection] = useState(null);

    const [bomTreeData, setBomTreeData] = useState([]);
    const [currentPdfLabel, setCurrentPdfLabel] = useState("");
    const treeData = useMemo(() => {
        if (!machineNumber && !machineName) {
            return bomTreeData?.length ? bomTreeData : [];
        }

        const rootNode = {
            nodeId: `${MACHINE_NODE_PREFIX}-${machineNumber || machine_id || "root"}`,
            productName: machineName || machineNumber || "Machine",
            productNumber: machineNumber || machine_id || "Machine",
            productId: machine?.machine_id ?? null,
            children: bomTreeData ?? [],
        };

        return [rootNode];
    }, [bomTreeData, machineName, machineNumber, machine_id, machine]);
    const bomIndex = useMemo(() => indexBomTree(treeData), [treeData]);

    const [showMenuBox, setShowMenuBox] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [innerTab, setInnerTab] = useState(t('MENU_ITEMS.ALL'));
    const [helpOpen, setHelpOpen] = useState(false);
    const [ccrOpen, setCcrOpen] = useState(false);
    const [ccrPrefill] = useState(null);

    const [treeSearchTerm, setTreeSearchTerm] = useState("");
    const [treeSearchMatches, setTreeSearchMatches] = useState([]);
    const [treeSearchIndex, setTreeSearchIndex] = useState(0);
    const [expandedNodes, setExpandedNodes] = useState([]);
    const [selectedNodeId, setSelectedNodeId] = useState(null);

    const [subSearchTerm, setSubSearchTerm] = useState("");
    const [rsplSearchTerm, setRsplSearchTerm] = useState("");

    const [loading, setLoading] = useState(true);
    const [loadingStep, setLoadingStep] = useState(0);
    const [percentage, setPercentage] = useState(0);
    const [progressTarget, setProgressTarget] = useState(0);
    const [error, setError] = useState(null);

    const [pdfUrl, setPdfUrl] = useState("");
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(DEFAULT_ZOOM);
    const [pdfPan, setPdfPan] = useState({ x: 0, y: 0 });
    const [isPanningPdf, setIsPanningPdf] = useState(false);

    const [selectedPart, setSelectedPart] = useState(null);
    const [showPartMenu, setShowPartMenu] = useState(false);
    const [partMenuDelta, setPartMenuDelta] = useState({ x: 0, y: 0 });
    const [isDraggingPartMenu, setIsDraggingPartMenu] = useState(false);

    const [cartItems, setCartItems] = useState([]);
    const [requestItems, setRequestItems] = useState([]);
    const [cartOpen, setCartOpen] = useState(false);
    const [orderingItems, setOrderingItems] = useState(false);

    const handleHelpClick = () => {
        setHelpOpen(true);
    };

    const level1SubAssemblies = useMemo(() => {
        if (!treeData?.length) return [];
        if (treeData.length === 1 && treeData[0]?.children) {
            return treeData[0].children;
        }
        return treeData;
    }, [treeData]);

    const filteredSubAssemblies = useMemo(() => {
        if (!subSearchTerm) return level1SubAssemblies;
        const term = subSearchTerm.trim().toLowerCase();
        return (level1SubAssemblies || []).filter((node) => {
            const nodeName = (
                node.productName ||
                node.product_name ||
                node.productNumber ||
                node.product_number ||
                node.nodeId ||
                ""
            )
                .trim()
                .toLowerCase();
            const productRef =
                node?.productRef !== undefined && node?.productRef !== null
                    ? String(node.productRef).trim().toLowerCase()
                    : "";

            const matchedProduct =
                products.find((p) => {
                    const num = p.product_number?.trim().toLowerCase();
                    const pname = p.product_name?.trim().toLowerCase();
                    const pid =
                        p.product_id !== undefined && p.product_id !== null
                            ? String(p.product_id).trim().toLowerCase()
                            : "";
                    return (
                        (productRef && productRef === num) ||
                        (productRef && productRef === pid) ||
                        nodeName === num ||
                        nodeName === pname ||
                        nodeName.startsWith(num || "") ||
                        num?.startsWith(nodeName) ||
                        (num && nodeName.includes(num)) ||
                        (pname && nodeName.startsWith(pname)) ||
                        (pname && nodeName.includes(pname))
                    );
                }) || null;

            const haystacks = [
                node.productName,
                node.product_name,
                node.productNumber,
                node.product_number,
                node.productRef,
                node.nodeId,
                matchedProduct?.product_name,
                matchedProduct?.product_number,
            ]
                .filter(Boolean)
                .map((value) => String(value).toLowerCase());

            return haystacks.some((value) => value.includes(term));
        });
    }, [level1SubAssemblies, subSearchTerm, products]);

    const filteredTree = useMemo(
        () => filterTreeByTerm(treeData, treeSearchTerm),
        [treeData, treeSearchTerm]
    );

    const filteredRspl = useMemo(() => {
        const term = rsplSearchTerm.trim().toLowerCase();
        return products.filter((product) => {
            const matchesType =
                innerTab === t('MENU_ITEMS.ALL') ||
                normalizeRsplType(product.ProductType) === normalizeRsplType(innerTab);
            if (!matchesType) return false;
            if (!term) return true;
            const haystacks = [
                product.product_name,
                product.product_number,
                product.ProductType,
            ]
                .filter(Boolean)
                .map((item) => item.toLowerCase());
            return haystacks.some((value) => value.includes(term));
        });
    }, [products, rsplSearchTerm, innerTab]);

    useEffect(() => {
        setPdfPan({ x: 0, y: 0 });
    }, [pdfUrl]);

    const machine2DAssetMap = useMemo(() => {
        const map = new Map();
        const addEntry = (key, path) => {
            const normalizedKey = normalizeLookupKey(key);
            if (!normalizedKey || !path) return;
            map.set(normalizedKey, path);
        };

        if (machine?.model_data?.["2dfile"]) {
            addEntry(machine?.machine_number, machine.model_data["2dfile"]);
            addEntry(machine?.machine_name, machine.model_data["2dfile"]);
        }

        (machine?.model_data?.boms || []).forEach((bom) => {
            addEntry(bom?.product_number, bom?.["2dfile"]);
        });

        return map;
    }, [machine]);

    const product2DAssetMap = useMemo(() => {
        const map = new Map();
        const addEntry = (key, path) => {
            const normalizedKey = normalizeLookupKey(key);
            if (!normalizedKey || !path) return;
            map.set(normalizedKey, path);
        };

        products.forEach((product) => {
            addEntry(product?.product_number, product?.twoDFilePath);
            addEntry(product?.product_name, product?.twoDFilePath);
            (product?.asset_data?.boms || []).forEach((bom) => {
                addEntry(bom?.product_number, bom?.["2dfile"]);
            });
        });

        return map;
    }, [products]);

    const convertAssetPathToUrl = useCallback((rawPath) => {
        if (!rawPath || typeof rawPath !== "string") return null;
        let normalized = rawPath.trim();
        if (!normalized) return null;
        if (/^https?:\/\//i.test(normalized)) return normalized;
        if (!/\.pdf$/i.test(normalized)) return null;

        normalized = normalized.replace(/\\/g, "/");
        normalized = normalized.replace(/^https?:\/\/[^/]+/i, "");
        normalized = normalized.replace(/^\/?stock-models\//i, "");
        normalized = normalized.replace(/^\/?models\//i, "");
        normalized = normalized.replace(/^\/?modelfiles\//i, "");
        normalized = normalized.replace(/^\/+/i, "");

        if (!/^2d\//i.test(normalized)) {
            normalized = `2D/${normalized}`;
        }

        const finalPath = `/models/${normalized}`.replace(/\/{2,}/g, "/");
        return `${SERVER_BASE_URL}${finalPath}`;
    }, []);

    const findPathByFilename = useCallback(
        (token) => {
            if (!token) return null;
            const normalized = token.trim().toLowerCase();
            if (!normalized) return null;

            const variants = new Set([normalized]);
            if (normalized.includes(".")) {
                variants.add(normalized.replace(/\.[^/.]+$/, ""));
            }

            const compare = (path) => {
                if (!path) return null;
                const base = extractFilenameFromPath(path).toLowerCase();
                const baseWithoutExt = base.replace(/\.[^/.]+$/, "");
                if (variants.has(base) || variants.has(baseWithoutExt)) {
                    return path;
                }
                return null;
            };

            for (const path of product2DAssetMap.values()) {
                const match = compare(path);
                if (match) return match;
            }
            for (const path of machine2DAssetMap.values()) {
                const match = compare(path);
                if (match) return match;
            }
            return null;
        },
        [product2DAssetMap, machine2DAssetMap]
    );

    const resolveAssetUrlFromCache = useCallback(
        (identifier) => {
            const variants = buildLookupVariants(identifier);
            for (const variant of variants) {
                const cachedPath =
                    product2DAssetMap.get(variant) ||
                    machine2DAssetMap.get(variant);
                if (!cachedPath) continue;
                const resolvedUrl = convertAssetPathToUrl(cachedPath);
                if (resolvedUrl) {
                    return resolvedUrl;
                }
            }
            const filenameMatchPath = findPathByFilename(identifier);
            if (filenameMatchPath) {
                return convertAssetPathToUrl(filenameMatchPath);
            }
            return null;
        },
        [
            product2DAssetMap,
            machine2DAssetMap,
            convertAssetPathToUrl,
            findPathByFilename,
        ]
    );

    const allNodeIds = useMemo(
        () => bomIndex.flatNodes.map((node) => node.nodeId),
        [bomIndex.flatNodes]
    );

    const findMatchingProductForPart = useCallback(
        (identifier, productRef = null) => {
            if (!products?.length) return null;
            const ref = (identifier || "").trim().toLowerCase();
            const nodeRef =
                productRef !== undefined && productRef !== null
                    ? String(productRef).trim().toLowerCase()
                    : "";
            return (
                products.find((p) => {
                    const num = p.product_number?.trim().toLowerCase();
                    const name = p.product_name?.trim().toLowerCase();
                    const pid =
                        p.product_id !== undefined && p.product_id !== null
                            ? String(p.product_id).trim().toLowerCase()
                            : "";
                    return (
                        (nodeRef && nodeRef === num) ||
                        (nodeRef && nodeRef === pid) ||
                        ref === num ||
                        ref === name ||
                        ref.startsWith(num || "") ||
                        num?.startsWith(ref) ||
                        (num && ref.includes(num)) ||
                        (name && ref.startsWith(name)) ||
                        (name && ref.includes(name))
                    );
                }) || null
            );
        },
        [products]
    );

    const resolveBomNodeMeta = useCallback(
        (node = {}) => {
            const productRefFallback =
                node?.productRef !== undefined && node?.productRef !== null
                    ? String(node.productRef)
                    : "";
            const baseName =
                node.productName ||
                node.product_name ||
                node.productNumber ||
                node.product_number ||
                node.nodeId ||
                "";
            const matched = findMatchingProductForPart(
                baseName,
                node?.productRef
            );
            const partName =
                matched?.product_name ||
                node.productName ||
                node.product_name ||
                productRefFallback ||
                "Node";
            const partNumber =
                node.productNumber ||
                node.product_number ||
                matched?.product_number ||
                productRefFallback ||
                "";

            return {
                partName,
                partNumber,
                description: node.description ? String(node.description) : "",
            };
        },
        [findMatchingProductForPart]
    );

    const scrollPartsTreeToTop = useCallback(() => {
        partsTreeScrollRef.current?.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    const scrollSubAssembliesToTop = useCallback(() => {
        subAssembliesScrollRef.current?.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    const scrollRsplToTop = useCallback(() => {
        rsplScrollRef.current?.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    const normalizePartMeta = useCallback(
        (part = {}) => {
            const fallbackNumber =
                part.part_number ||
                part.partNumber ||
                part.product_number ||
                null;
            let normalizedNumber = fallbackNumber
                ? String(fallbackNumber)
                : null;
            let normalizedProductId =
                part.product_id || part.productId || part.ProductID || null;
            let normalizedPrice = part.price;
            let normalizedPurchasable =
                part.purchasable !== undefined && part.purchasable !== null
                    ? part.purchasable
                    : null;

            if (
                normalizedNumber &&
                (!normalizedProductId ||
                    normalizedPrice == null ||
                    normalizedPurchasable == null)
            ) {
                const match = products?.find(
                    (p) =>
                        p.product_number?.toString().trim() ===
                        normalizedNumber?.toString().trim()
                );
                if (match) {
                    if (!normalizedProductId)
                        normalizedProductId = match.product_id;
                    if (normalizedPrice == null)
                        normalizedPrice = getSitePrice(match);
                    if (normalizedPurchasable == null)
                        normalizedPurchasable = match.purchasable;
                }
            }

            return {
                ...part,
                part_number: normalizedNumber,
                product_id: normalizedProductId,
                price: normalizedPrice,
                purchasable: normalizedPurchasable,
                isOrderable:
                    normalizedProductId != null ||
                    normalizedPurchasable !== null,
            };
        },
        [products, getSitePrice]
    );

    const addToCart = useCallback(
        (part) => {
            const normalized = normalizePartMeta(part);
            if (!normalized.part_number) return;
            setCartItems((prev) => {
                const exists = prev.find(
                    (item) => item.part_number === normalized.part_number
                );
                if (exists) return prev;
                return [
                    ...prev,
                    {
                        ...normalized,
                        qty: normalized.qty ?? 1,
                    },
                ];
            });
        },
        [normalizePartMeta]
    );

    const addToRequestList = useCallback(
        (part) => {
            const normalized = normalizePartMeta(part);
            if (!normalized.part_number) return;
            setRequestItems((prev) => {
                const exists = prev.find(
                    (item) => item.part_number === normalized.part_number
                );
                if (exists) return prev;
                return [...prev, normalized];
            });
        },
        [normalizePartMeta]
    );

    const removeCartItem = (partNumber) =>
        setCartItems((prev) =>
            prev.filter((item) => item.part_number !== partNumber)
        );
    const removeRequestItem = (partNumber) =>
        setRequestItems((prev) =>
            prev.filter((item) => item.part_number !== partNumber)
        );

    const incrementQty = (partNumber) =>
        setCartItems((prev) =>
            prev.map((item) =>
                item.part_number === partNumber
                    ? { ...item, qty: item.qty + 1 }
                    : item
            )
        );
    const decrementQty = (partNumber) =>
        setCartItems((prev) =>
            prev.map((item) =>
                item.part_number === partNumber && item.qty > 1
                    ? { ...item, qty: item.qty - 1 }
                    : item
            )
        );

    const isInCart = useCallback(
        (pn) => cartItems.some((item) => item.part_number === pn),
        [cartItems]
    );
    const isInRequest = useCallback(
        (pn) => requestItems.some((item) => item.part_number === pn),
        [requestItems]
    );

    const handleAddAllProductType = useCallback(
        (type) => {
            const normalizedType = normalizeRsplType(type);
            const exactParts = productsByType[type] || [];
            const fallbackParts = Object.entries(productsByType).find(
                ([label]) => normalizeRsplType(label) === normalizedType
            )?.[1] || [];
            const parts = exactParts.length ? exactParts : fallbackParts;

            if (!parts || parts.length === 0) {
                Swal.fire({
                    icon: "info",
                    title: `<div style='text-align:center;'>${t("UI_TEXT.NO_PARTS_FOUND")}</div>`,
                    html: `<div style='text-align:center;'>${t("UI_TEXT.THERE_ARE_NO_RSPL_PARTS_FOR_TYPE", { type })}</div>`,
                });
                return;
            }

            const normalizedParts = parts
                .map((part) =>
                    normalizePartMeta({
                        ...part,
                        part_number: part?.product_number,
                        product_id: part?.product_id,
                        price: getSitePrice(part),
                        purchasable: part?.purchasable,
                    })
                )
                .filter((part) => !!part?.part_number && !!part?.product_id);

            const cartToAdd = normalizedParts;
            const requestToAdd = [];

            setCartItems((prev) => {
                const existing = new Set(prev.map((item) => String(item?.part_number || "").trim()));
                const toAdd = cartToAdd
                    .filter((item) => {
                        const key = String(item?.part_number || "").trim();
                        if (!key || existing.has(key)) return false;
                        existing.add(key);
                        return true;
                    })
                    .map((item) => ({ ...item, qty: 1 }));
                return [...prev, ...toAdd];
            });

            setRequestItems((prev) => {
                const existing = new Set(prev.map((item) => String(item?.part_number || "").trim()));
                const toAdd = requestToAdd.filter((item) => {
                    const key = String(item?.part_number || "").trim();
                    if (!key || existing.has(key)) return false;
                    existing.add(key);
                    return true;
                });
                return [...prev, ...toAdd];
            });

            Swal.fire({
                icon: "success",
                title: `<div style='text-align:center;'>${t("GENERAL.ADDED", { defaultValue: "Added" })}</div>`,
                html: `
                    <div style='text-align:center;'>
                        <b>${t("UI_TEXT.PURCHASABLE_PARTS")}:</b> ${cartToAdd.length}<br/>
                        <b>${t("UI_TEXT.NON_PURCHASABLE_PARTS")}:</b> ${requestToAdd.length}
                    </div>
                `,
                confirmButtonText: t("GENERAL.OK"),
            });
        },
        [productsByType, t, normalizePartMeta, getSitePrice]
    );

    const handleClearAll = () => {
        setCartItems([]);
        setRequestItems([]);
    };

    const handleClearCart = () => {
        setCartItems([]);
    };

    const handleClearRequestList = () => {
        setRequestItems([]);
    };

    const handleOrderItems = async () => {
        if (orderingItems) return;
        if (cartItems.length === 0 && requestItems.length === 0) {
            Swal.fire({
                icon: "info",
                title: "<div style='text-align:center;'>No Items to Order</div>",
                html: "<div style='text-align:center;'>Cart and request list are empty.</div>",
            });
            return;
        }
        setOrderingItems(true);
        try {

        const expandedCart = [];
        const expandedRequest = [];

        const expandGroup = (group) => {
            const type = group.group_type;
            const normalizedType = normalizeRsplType(type);
            const exactParts = productsByType[type] || [];
            const fallbackParts = Object.entries(productsByType).find(
                ([label]) => normalizeRsplType(label) === normalizedType
            )?.[1] || [];
            const parts = exactParts.length ? exactParts : fallbackParts;
            parts.forEach((p) => {
                const item = {
                    product_id: p.product_id,
                    part_number: p.product_number,
                };
                if (p.purchasable) {
                    expandedCart.push({
                        ...item,
                        price: getSitePrice(p),
                        qty: 1,
                    });
                } else {
                    expandedRequest.push(item);
                }
            });
        };

        cartItems.forEach((item) => {
            if (item.is_group) {
                expandGroup(item);
            } else {
                expandedCart.push({
                    product_id: item.product_id,
                    part_number: item.part_number,
                    qty: item.qty,
                    price: item.price,
                });
            }
        });

        requestItems.forEach((item) => {
            if (item.is_group) {
                expandGroup(item);
            } else {
                expandedRequest.push({
                    product_id: item.product_id,
                    part_number: item.part_number,
                });
            }
        });

        const rlProducts = expandedRequest
            .filter((item) => item?.product_id)
            .map((item) => `${item.product_id}:1`)
            .join(",");
        const cartProducts = expandedCart
            .filter((item) => item?.product_id)
            .map((item) => `${item.product_id}:${item?.qty ?? 1}`)
            .join(",");

        if (!rlProducts && !cartProducts) {
            Swal.fire({
                icon: "info",
                title: "<div style='text-align:center;'>No Orderable Parts</div>",
                html: "<div style='text-align:center;'>Unable to translate selected items into products.</div>",
            });
            return;
        }

        let storedUser = null;
        try {
            const rawUser = sessionStorage.getItem("user");
            storedUser = rawUser ? JSON.parse(rawUser) : null;
        } catch (storageError) {
            console.error("Unable to read session user:", storageError);
        }

        const userId = user?.user_id || storedUser?.user_id;

        if (!userId || !selectedSite || !machine_id) {
            Swal.fire({
                icon: "warning",
                title: "<div style='text-align:center;'>Missing Information</div>",
                html: "<div style='text-align:center;'>User, site, or machine information is unavailable.</div>",
            });
            return;
        }

        const payload = {
            userId,
            selectedSite,
            AddToRequest: rlProducts,
            AddToCart: cartProducts,
            machine_id,
        };

        try {
            const response = await addModelCartItems(payload);
            if (response?.status === false) {
                throw new Error(response?.message || "Unable to process order.");
            }

            handleClearAll();
            setCartOpen(false);

            const destination = cartProducts ? "/cart" : "/requests";
            const destinationLabel = cartProducts ? "Quote" : "Request List";
            const totalCartItems = expandedCart.length;
            const totalRequestItems = expandedRequest.length;
            const redirectSeconds = 5;
            let remainingSeconds = redirectSeconds;
            let countdownInterval = null;

            const redirectFromSwal = () => {
                if (destination === "/cart") {
                    const params = new URLSearchParams({
                        mvSource: "pdfviewer",
                        mvCartCount: String(totalCartItems),
                        mvRequestCount: String(totalRequestItems),
                    });
                    const url = `${destination}?${params.toString()}`;
                    window.location.href = url;
                } else {
                    navigate(destination);
                }
            };

            Swal.fire({
                icon: "success",
                title: "<div style='text-align:center;'>Items Submitted</div>",
                html: `
                    <div style='text-align:center;'>
                        <b>Added to Quote:</b> ${totalCartItems}<br/>
                        <b>Added to Request List:</b> ${totalRequestItems}<br/><br/>
                        Redirecting to ${destinationLabel} in <b><span id="swal-cart-countdown">${remainingSeconds}</span>s</b>.
                    </div>
                `,
                allowOutsideClick: false,
                allowEscapeKey: false,
            }).then(() => {
                clearInterval(countdownInterval);
                redirectFromSwal();
            });

            countdownInterval = setInterval(() => {
                remainingSeconds -= 1;
                const countdownEl =
                    document.getElementById("swal-cart-countdown");
                if (countdownEl) {
                    countdownEl.textContent = `${remainingSeconds}`;
                }
                if (remainingSeconds <= 0) {
                    clearInterval(countdownInterval);
                    redirectFromSwal();
                }
            }, 1000);
        } catch (orderError) {
            console.error("Unable to order items:", orderError);
            Swal.fire({
                icon: "error",
                title: "<div style='text-align:center;'>Quote Failed</div>",
                html: "<div style='text-align:center;'>Unable to push selected items to quote/request list.</div>",
            });
        }
        } finally {
            setOrderingItems(false);
        }
    };

    const expandAncestors = useCallback(
        (nodeId) => {
            if (!nodeId) return;
            const ancestors = collectAncestorIds(nodeId, bomIndex.nodeMap);
            setExpandedNodes((prev) => {
                const next = new Set(prev);
                ancestors.forEach((id) => next.add(id));
                return Array.from(next);
            });
        },
        [bomIndex.nodeMap]
    );

    const handleTreeNodeToggle = (nodeId) => {
        setExpandedNodes((prev) =>
            prev.includes(nodeId)
                ? prev.filter((id) => id !== nodeId)
                : [...prev, nodeId]
        );
    };

    const buildCandidatesForNode = useCallback(
        (node) => {
            const candidates = [];
            if (node?.productNumber) {
                candidates.push(node.productNumber);
            }
            if (node?.product_number) {
                candidates.push(node.product_number);
            }
            if (node?.parentProductNumber) {
                candidates.push(node.parentProductNumber);
            }
            if (node?.parent_product_number) {
                candidates.push(node.parent_product_number);
            }
            if (node?.nodeId) {
                const ancestors = collectAncestorIds(
                    node.nodeId,
                    bomIndex.nodeMap
                );
                ancestors.forEach((ancestorId) => {
                    const ancestorNode = bomIndex.nodeMap.get(ancestorId);
                    if (ancestorNode?.productNumber) {
                        candidates.push(ancestorNode.productNumber);
                    }
                    if (ancestorNode?.product_number) {
                        candidates.push(ancestorNode.product_number);
                    }
                });
            }
            if (machineNumber) {
                candidates.push(machineNumber);
            }
            return Array.from(
                new Set(
                    candidates
                        .filter(Boolean)
                        .map((value) => String(value).trim())
                )
            );
        },
        [bomIndex.nodeMap, machineNumber]
    );

    const setProgressForStep = (step) => {
        setLoadingStep(step);
        const map = [30, 60, 85, 100];
        const target = map[step] ?? 100;
        setProgressTarget(target);
        if (step === 0) {
            setPercentage(0);
        } else {
            setPercentage((prev) => (prev > target ? target : prev));
        }
    };

    useEffect(() => {
        if (!loading || progressTarget <= 0) return;

        const interval = setInterval(() => {
            setPercentage((prev) => {
                if (prev >= progressTarget) return prev;
                const bump = progressTarget >= 90 ? 4 : 6;
                return Math.min(prev + bump, progressTarget);
            });
        }, 90);

        return () => clearInterval(interval);
    }, [loading, progressTarget]);

    const loadPdfForCandidates = useCallback(
        async (candidates = []) => {
            if (!candidates?.length || !machineNumber) return;
            setPdfError(null);
            setNumPages(null);
            setPageNumber(1);
            setCurrentPdfLabel("");

            for (const candidate of candidates) {
                const cachedUrl = resolveAssetUrlFromCache(candidate);
                if (cachedUrl) {
                    setPdfUrl(cachedUrl);
                    setProgressForStep(3);
                    setLoading(false);
                    setPdfLoading(false);
                    setPdfError(null);
                    setCurrentPdfLabel(formatPdfLabel(candidate, cachedUrl));
                    return candidate;
                }
            }

            setPdfUrl("");
            setPdfLoading(true);

            for (const candidate of candidates) {
                try {
                    const response = await process2DModelFile({
                        filename: candidate,
                        machinenumber: machineNumber,
                    });
                    if (response?.status === 200 && response?.data?.url) {
                        const relativeUrl = response.data.url;
                        const fileUrl = relativeUrl.startsWith("http")
                            ? relativeUrl
                            : `${SERVER_BASE_URL}${relativeUrl}`;
                        setPdfUrl(fileUrl);
                        setProgressForStep(3);
                        setLoading(false);
                        setPdfLoading(false);
                        setPdfError(null);
                        setCurrentPdfLabel(
                            formatPdfLabel(candidate, relativeUrl)
                        );
                        return candidate;
                    }
                } catch (err) {
                    console.warn(
                        `Unable to load PDF for ${candidate}:`,
                        err
                    );
                }
            }

            setPdfLoading(false);
            setPdfUrl("");
            setPdfError(`No Drawing was found related to the selected ${t('UI_TEXT.PART')} ${selectedPart?.part_number || ""}`);
            setLoading(false);
            setProgressForStep(3);
            setCurrentPdfLabel("");
            return null;
        },
        [machineNumber, resolveAssetUrlFromCache]
    );

    const handlePartSelection = useCallback(
        (nodeOrPart) => {
            if (!nodeOrPart) {
                setSelectedPart(null);
                setShowPartMenu(false);
                setSelectedNodeId(null);
                return;
            }

            const identifier =
                nodeOrPart.productNumber ||
                nodeOrPart.product_number ||
                nodeOrPart.part_number ||
                nodeOrPart.productName ||
                nodeOrPart.part_name ||
                "";
            const match = findMatchingProductForPart(identifier);

            const partData = {
                part_name:
                    match?.product_name ||
                    nodeOrPart.productName ||
                    nodeOrPart.part_name ||
                    nodeOrPart.product_number ||
                    nodeOrPart.partNumber ||
                    "Selected Part",
                part_number:
                    match?.product_number ||
                    nodeOrPart.productNumber ||
                    nodeOrPart.part_number ||
                    null,
                part_type: nodeOrPart.ProductType || nodeOrPart.bomLevel || "",
                url: match ? `/machines/${machine_id}/${match.product_id}` : "",
                price: match ? getSitePrice(match) : null,
                purchasable: match?.purchasable ?? 0,
                isOrderable: !!match,
                product_id: match?.product_id ?? null,
            };

            setSelectedPart(partData);
            setShowPartMenu(true);
        },
        [findMatchingProductForPart, getSitePrice]
    );

    const selectNodeByProductNumber = useCallback(
        (productNumber) => {
            if (
                !productNumber ||
                !bomIndex?.productNumberToNodeId ||
                !bomIndex?.nodeMap
            )
                return false;
            const normalized = String(productNumber).trim();
            if (!normalized) return false;
            const lookupKeys = [
                normalized,
                normalized.toUpperCase(),
                normalized.toLowerCase(),
            ];
            let nodeId = null;
            for (const key of lookupKeys) {
                const found = bomIndex.productNumberToNodeId.get(key);
                if (found) {
                    nodeId = found;
                    break;
                }
            }
            if (!nodeId) return false;
            const node = bomIndex.nodeMap.get(nodeId);
            setSelectedNodeId(nodeId);
            expandAncestors(nodeId);
            if (node) {
                handlePartSelection(node);
            }
            return true;
        },
        [
            bomIndex.productNumberToNodeId,
            bomIndex.nodeMap,
            expandAncestors,
            handlePartSelection,
        ]
    );

    const handleResolvedCandidate = useCallback(
        (candidate) => {
            if (!candidate) return;
            const success = selectNodeByProductNumber(candidate);
            if (success) {
                setPendingTreeSelection(null);
            }
        },
        [selectNodeByProductNumber]
    );

    const handlePdfWheel = useCallback(
        (event) => {
            if (!pdfUrl) return;
            event.preventDefault();
            const direction = event.deltaY < 0 ? 1 : -1;
            pendingWheelDirectionRef.current = direction;
            if (zoomWheelFrameRef.current) return;
            zoomWheelFrameRef.current = window.requestAnimationFrame(() => {
                const nextDirection = pendingWheelDirectionRef.current;
                pendingWheelDirectionRef.current = 0;
                setScale((prev) =>
                    Math.min(
                        MAX_ZOOM,
                        Math.max(MIN_ZOOM, prev + nextDirection * ZOOM_STEP)
                    )
                );
                zoomWheelFrameRef.current = null;
            });
        },
        [pdfUrl]
    );

    useEffect(() => {
        return () => {
            if (zoomWheelFrameRef.current) {
                window.cancelAnimationFrame(zoomWheelFrameRef.current);
                zoomWheelFrameRef.current = null;
            }
        };
    }, []);

    const handlePdfMouseDown = (event) => {
        if (!pdfUrl) return;
        event.preventDefault();
        pdfDragStartRef.current = {
            mouseX: event.clientX,
            mouseY: event.clientY,
            panX: pdfPan.x,
            panY: pdfPan.y,
        };
        setIsPanningPdf(true);
    };

    const handlePdfMouseMove = (event) => {
        if (!isPanningPdf) return;
        event.preventDefault();
        const deltaX = event.clientX - pdfDragStartRef.current.mouseX;
        const deltaY = event.clientY - pdfDragStartRef.current.mouseY;
        setPdfPan({
            x: pdfDragStartRef.current.panX + deltaX,
            y: pdfDragStartRef.current.panY + deltaY,
        });
    };

    const stopPdfPan = () => {
        if (!isPanningPdf) return;
        setIsPanningPdf(false);
    };

    const handleTreeNodeSelect = (node) => {
        if (!node) return;
        setSelectedNodeId(node.nodeId);
        handlePartSelection(node);
        expandAncestors(node.nodeId);
        const candidates = buildCandidatesForNode(node);
        loadPdfForCandidates(candidates).then((resolved) => {
            if (resolved) {
                handleResolvedCandidate(resolved);
            }
        });
    };

    const handleSubAssemblyClick = (node) => {
        handleTreeNodeSelect(node);
    };

    const handleRsplPartClick = (product) => {
        handlePartSelection({
            ...product,
            part_name: product.product_name,
            part_number: product.product_number,
            ProductType: product.ProductType,
        });
        loadPdfForCandidates([
            product.product_number,
            product.product_name,
            machineNumber,
        ]).then((resolved) => {
            if (resolved) {
                handleResolvedCandidate(resolved);
            }
        });
    };

    const handleTreeSearchSubmit = () => {
        if (!treeSearchMatches.length) return;
        setTreeSearchIndex((prev) => {
            const nextIndex =
                treeSearchMatches.length === 1
                    ? 0
                    : (prev + 1) % treeSearchMatches.length;
            const nodeId = treeSearchMatches[nextIndex];
            expandAncestors(nodeId);
            setTimeout(() => {
                const element = document.getElementById(
                    `bom-node-${nodeId}`
                );
                element?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }, 50);
            return nextIndex;
        });
    };

    const handleTreeSearchClear = () => {
        setTreeSearchTerm("");
        setTreeSearchMatches([]);
        setTreeSearchIndex(0);
    };

    useEffect(() => {
        if (!treeSearchTerm) {
            setTreeSearchMatches([]);
            setTreeSearchIndex(0);
            return;
        }
        const normalized = treeSearchTerm.trim().toLowerCase();
        const matches = bomIndex.flatNodes
            .filter((node) => {
                const { partName, partNumber, description } = resolveBomNodeMeta(
                    node
                );
                const fields = [partName, partNumber, description, node.nodeId]
                    .filter(Boolean)
                    .map((value) => String(value).toLowerCase());
                return fields.some((value) => value.includes(normalized));
            })
            .map((node) => node.nodeId);
        setTreeSearchMatches(matches);
        if (matches.length) {
            setTreeSearchIndex(0);
            expandAncestors(matches[0]);
        }
    }, [treeSearchTerm, bomIndex.flatNodes, expandAncestors, resolveBomNodeMeta]);

    useEffect(() => {
        if (treeData?.length) {
            setExpandedNodes(treeData.map((node) => node.nodeId));
        } else {
            setExpandedNodes([]);
        }
    }, [treeData]);

    useEffect(() => {
        if (!pendingTreeSelection) return;
        const success = selectNodeByProductNumber(pendingTreeSelection);
        if (success) {
            setPendingTreeSelection(null);
        }
    }, [pendingTreeSelection, selectNodeByProductNumber]);

    const fetchData = useCallback(async () => {
        if (!machine_id || !selectedSiteData?.site_id || !selectedSite) {
            return;
        }

        setLoading(true);
        setError(null);
        setProgressForStep(0);

        const payload = {
            machine_id,
            site_number: selectedSite,
        };

        try {
            const [machineResponse, productsResponse] = await Promise.all([
                getMachineByNumberOrId({
                    machine_id,
                    site_id: selectedSiteData.site_id,
                }),
                getProductsByMachineId(payload),
            ]);

            let hadError = false;

            let machineData = null;

            if (machineResponse?.status && machineResponse?.data?.length) {
                machineData = machineResponse.data[0];
                setMachine(machineData);
                setMachineName(machineData.machine_name);
                setMachineNumber(machineData.machine_number);
                setProgressForStep(1);
            } else {
                hadError = true;
                setError("Failed fetching machine details.");
            }

            if (productsResponse?.status) {
                const { products: machineProducts, bomTree } =
                    normalizeMachineProductsResponse(productsResponse);

                const productsWithMeta = machineProducts.map((product) => {
                    const productType = getRsplLabelForCode(
                        product.product_rspl_code,
                        rsplMapping,
                        t('UI_TEXT.OTHER')
                    );
                    const imageUrl = buildProductImageUrl(product);
                    const twoDAssetPath =
                        product?.asset_data?.["2dfile"] || null;

                    return {
                        ...product,
                        ProductType: productType,
                        productImageURL: imageUrl,
                        twoDFilePath: twoDAssetPath,
                    };
                });

                const typeSet = rsplTypeLabels;

                const grouped = {};
                typeSet.forEach((type) => {
                    grouped[type] = productsWithMeta.filter(
                        (p) => p.ProductType === type
                    );
                });

                setProducts(productsWithMeta);
                setProductTypes(typeSet);
                setProductsByType(grouped);
                setBomTreeData(bomTree || []);
                setProgressForStep(2);

                const bomCandidate =
                    (machineData?.model_data?.boms || []).find(
                        (bom) => !!bom?.["2dfile"]
                    ) || null;
                const firstProductWithPdf = productsWithMeta.find(
                    (product) => !!product.twoDFilePath
                );
                let derivedCandidate = null;
                let derivedSelection = null;
                // Default first load to the first BOM PDF candidate.
                if (bomCandidate?.product_number) {
                    derivedCandidate = bomCandidate.product_number;
                    derivedSelection = bomCandidate.product_number;
                } else if (firstProductWithPdf?.product_number) {
                    derivedCandidate = firstProductWithPdf.product_number;
                    derivedSelection = firstProductWithPdf.product_number;
                } else if (machineData?.model_data?.["2dfile"]) {
                    derivedCandidate =
                        machineData?.machine_number || machine_id || null;
                }
                if (!derivedCandidate) {
                    derivedCandidate =
                        machineData?.machine_number || machine_id || null;
                }
                const normalizedDecoded = normalizeLookupKey(decodedFileName);
                const normalizedMachineNumber = normalizeLookupKey(
                    machineData?.machine_number
                );
                const normalizedMachineName = normalizeLookupKey(
                    machineData?.machine_name
                );
                const decodedMatchesPartWithPdf = productsWithMeta.some(
                    (product) =>
                        !!product.twoDFilePath &&
                        normalizeLookupKey(product?.product_number) === normalizedDecoded
                );
                const decodedMatchesBomWithPdf = (machineData?.model_data?.boms || []).some(
                    (bom) =>
                        !!bom?.["2dfile"] &&
                        normalizeLookupKey(bom?.product_number) === normalizedDecoded
                );
                const isSpecificPartPdfRequest =
                    Boolean(normalizedDecoded) &&
                    normalizedDecoded !== normalizedMachineNumber &&
                    normalizedDecoded !== normalizedMachineName &&
                    (decodedMatchesPartWithPdf || decodedMatchesBomWithPdf);

                const preferredCandidate =
                    isSpecificPartPdfRequest ? decodedFileName : derivedCandidate;
                if (preferredCandidate) {
                    setDefaultPdfCandidate(preferredCandidate);
                    initialFileRef.current = preferredCandidate;
                }
                if (isSpecificPartPdfRequest) {
                    setPendingTreeSelection(decodedFileName);
                } else if (derivedSelection) {
                    setPendingTreeSelection(derivedSelection);
                }
            } else {
                hadError = true;
                setError("Failed to fetch products.");
                setBomTreeData([]);
            }

            if (hadError) {
                setLoading(false);
            }
        } catch (fetchErr) {
            console.error("Error fetching PDF model viewer data:", fetchErr);
            setMachine(null);
            setProducts([]);
            setBomTreeData([]);
            setError("Failed fetching machine/products details.");
            setLoading(false);
        }
    }, [machine_id, selectedSiteData, selectedSite, rsplMapping, rsplTypeLabels, decodedFileName]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!machineNumber) return;
        const initialCandidates = [];
        if (defaultPdfCandidate) {
            initialCandidates.push(defaultPdfCandidate);
        }
        if (
            machineNumber &&
            !initialCandidates.includes(machineNumber)
        ) {
            initialCandidates.push(machineNumber);
        }
        if (!initialCandidates.length) return;
        loadPdfForCandidates(initialCandidates).then((resolved) => {
            if (resolved) {
                handleResolvedCandidate(resolved);
            }
        });
    }, [
        machineNumber,
        defaultPdfCandidate,
        loadPdfForCandidates,
        handleResolvedCandidate,
    ]);

    useEffect(() => {
        if (productTypes.length > 0) {
            setInnerTab(t('MENU_ITEMS.ALL'));
        } else {
            setInnerTab("");
        }
    }, [productTypes]);

    const handleDocumentLoadSuccess = ({ numPages: totalPages }) => {
        setNumPages(totalPages);
        setPageNumber(1);
    };

    const handleDocumentLoadError = (docError) => {
        console.error("Failed to load PDF document:", docError);
        setPdfError("Unable to load drawing.");
    };

    const handleHome = () => {
        setScale(DEFAULT_ZOOM);
        setPdfPan({ x: 0, y: 0 });
        setPageNumber(1);
        if (initialFileRef.current || machineNumber) {
            loadPdfForCandidates([
                initialFileRef.current,
                machineNumber,
            ]
                .filter(Boolean))
                .then((resolved) => {
                    if (resolved) {
                        handleResolvedCandidate(resolved);
                    }
                });
        }
    };

    const handlePrevPage = () =>
        setPageNumber((prev) => Math.max(1, prev - 1));
    const handleNextPage = () =>
        setPageNumber((prev) =>
            numPages ? Math.min(numPages, prev + 1) : prev + 1
        );
    const handleZoomIn = () =>
        setScale((prev) => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
    const handleZoomOut = () =>
        setScale((prev) => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
    const handleResetZoom = () => {
        setScale(DEFAULT_ZOOM);
        setPdfPan({ x: 0, y: 0 });
    };

    const handleCartClick = () => {
        setCartOpen((prev) => !prev);
    };

    const renderBomNodes = (nodes, depth = 0) => {
        if (!nodes) return null;
        return nodes.map((node) => {
            const nodeId = node.nodeId || node.child_key || `node-${depth}`;
            const original = bomIndex.nodeMap.get(nodeId) || node;
            const hasChildren =
                Array.isArray(original.children) &&
                original.children.length > 0;
            const isExpanded = expandedNodes.includes(nodeId);
            const isSelected = selectedNodeId === nodeId;
            const isMatch = treeSearchMatches.includes(nodeId);
            const isActiveMatch =
                isMatch && treeSearchMatches[treeSearchIndex] === nodeId;

            const { partName, partNumber, description } =
                resolveBomNodeMeta(original);

            return (
                <Box key={nodeId}>
                    <Box
                        id={`bom-node-${nodeId}`}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            pl: 1 + depth * 2,
                            pr: 1,
                            py: 0.5,
                            borderRadius: 1,
                            cursor: "pointer",
                            backgroundColor: isActiveMatch
                                ? "rgba(13,110,253,0.15)"
                                : isSelected
                                    ? "rgba(13,110,253,0.1)"
                                    : isMatch
                                        ? "rgba(25,118,210,0.08)"
                                        : "transparent",
                            "&:hover": {
                                backgroundColor: "rgba(25,118,210,0.08)",
                            },
                        }}
                        onClick={() => handleTreeNodeSelect(original)}
                    >
                        {hasChildren ? (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleTreeNodeToggle(nodeId);
                                }}
                            >
                                {isExpanded ? (
                                    <ExpandMore fontSize="small" />
                                ) : (
                                    <ChevronRight fontSize="small" />
                                )}
                            </IconButton>
                        ) : (
                            <Box sx={{ width: 32 }} />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="body1"
                                noWrap
                                sx={{
                                    fontWeight: isSelected ? "bold" : "normal",
                                }}
                            >
                                {partName}
                            </Typography>
                            {(partNumber || description) && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    sx={{ fontSize: 11 }}
                                >
                                    {partNumber ? `Part #: ${partNumber}` : ""}
                                    {description ? ` / ${description}` : ""}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {hasChildren && isExpanded && (
                        <Box>{renderBomNodes(original.children, depth + 1)}</Box>
                    )}
                </Box>
            );
        });
    };

    const rsplContent = filteredRspl.map((product) => (
        <Box
            key={product.product_number}
            onClick={() => handleRsplPartClick(product)}
            sx={{ cursor: "pointer" }}
        >
            <PartCard
                data={{
                    part_name: product.product_name,
                    part_number: product.product_number,
                    price: product.purchasable
                        ? getSitePrice(product)
                        : null,
                    purchasable: product.purchasable,
                    productImageURL: product.productImageURL,
                }}
            />
        </Box>
    ));

    const renderSubAssemblyCards = filteredSubAssemblies.map((node) => {
        const match = findMatchingProductForPart(
            node.productNumber || node.productName,
            node.productRef
        );
        return (
            <Box
                key={node.nodeId}
                onClick={() => handleSubAssemblyClick(node)}
                sx={{ cursor: "pointer" }}
            >
                <PartCard
                    data={{
                        part_name:
                            match?.product_name ||
                            node.productName ||
                            node.productNumber ||
                            node.nodeId,
                        part_number:
                            match?.product_number || node.productNumber,
                        price: match ? getSitePrice(match) : null,
                        purchasable: match?.purchasable ?? 0,
                        productImageURL: match?.productImageURL || null,
                    }}
                    isActive={selectedNodeId === node.nodeId}
                />
            </Box>
        );
    });

    const handleClose = () => {
        Swal.fire({
            title: t('UI_TEXT.ARE_YOU_SURE_YOU_WANT_TO_CLOSE'),
            text: t('UI_TEXT.ANY_ITEMS_ADDED_IN_THE_MODEL_CART_WILL_NOT_BE_ADDED_TO_THE_APPLICATION_CART'),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t('BUTTON.YES_CLOSE'),
            cancelButtonText: t('BUTTON.CANCEL'),
        }).then((result) => {
            if (result.isConfirmed) {
                navigate(returnPath);
            }
        });
    };

    const handleContactCcrClick = () => {
        setHelpOpen(false);
        setCcrOpen(true);
    };

    const helpSections = [
        {
            title: "Hamburger Menu",
            items: [
                {
                    icon: <MenuIcon fontSize="small" />,
                    title: "Menu Panel",
                    description:
                        `Open or close the left panel.\nSwitch between ${menuTabLabels.subAssemblies}, ${menuTabLabels.rspl}, and ${menuTabLabels.partsTree}.`,
                },
            ],
        },
        {
            title: "Part Menu",
            items: [
                {
                    icon: <Launch fontSize="small" />,
                    title: "Open Part",
                    description:
                        "Open the selected part's product page.\nAvailable when a link is provided.",
                },
                {
                    icon: <Close fontSize="small" />,
                    title: "Close Menu",
                    description:
                        "Dismiss the part menu popover.\nSelection stays active in the drawing.",
                },
                {
                    icon: <ShoppingCart fontSize="small" />,
                    title: t('BUTTONS.ADD_TO_CART'),
                    description:
                        "Add the selected part to the model cart.\nAdjust quantities in the cart drawer.",
                },
                {
                    icon: <Summarize fontSize="small" />,
                    title: "Add to Request",
                    description:
                        "Send non-purchasable parts to the request list.\nSubmit with other parts later.",
                },
            ],
        },
        {
            title: "Bottom Toolbar",
            items: [
                {
                    icon: <HomeIcon fontSize="small" />,
                    title: "Home",
                    description:
                        "Reload the default drawing and reset zoom.\nReturns to page 1.",
                },
                {
                    icon: <ArrowBackIosNew fontSize="small" />,
                    title: "Previous Page",
                    description:
                        "Go to the previous drawing page.\nDisabled on the first page.",
                },
                {
                    icon: <ArrowForwardIos fontSize="small" />,
                    title: "Next Page",
                    description:
                        "Go to the next drawing page.\nDisabled on the last page.",
                },
                {
                    icon: <ZoomIn fontSize="small" />,
                    title: "Zoom In",
                    description:
                        "Increase zoom for more detail.\nMouse wheel also works.",
                },
                {
                    icon: <ZoomOut fontSize="small" />,
                    title: "Zoom Out",
                    description:
                        "Decrease zoom for more context.\nMouse wheel also works.",
                },
                {
                    icon: <RestartAlt fontSize="small" />,
                    title: "Reset Zoom",
                    description:
                        "Return to the default zoom level.\nKeeps the current page.",
                },
                {
                    icon: <QuestionMark fontSize="small" />,
                    title: "Help",
                    description:
                        "Open this guide.\nQuick reference for controls.",
                },
            ],
        },
    ];

    const helpContent = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {helpSections.map((section) => (
                <Box key={section.title}>
                    <Typography variant="body1" fontWeight={600} mb={1}>
                        {section.title}
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 2,
                        }}
                    >
                        {section.items.map((item) => (
                            <Box
                                key={item.title}
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "flex-start",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: "50%",
                                        backgroundColor: "rgba(0,0,0,0.06)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    {item.icon}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                        {item.title}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ whiteSpace: "pre-line" }}
                                    >
                                        {item.description}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );

    const handlePartMenuMouseDown = useCallback(
        (event) => {
            event.preventDefault();
            const { clientX, clientY } = event;
            partMenuDragAnchorRef.current = {
                mouseX: clientX,
                mouseY: clientY,
                dx: partMenuDelta.x,
                dy: partMenuDelta.y,
            };
            setIsDraggingPartMenu(true);
        },
        [partMenuDelta.x, partMenuDelta.y]
    );

    useEffect(() => {
        if (!isDraggingPartMenu) return;

        const handleMouseMove = (event) => {
            const { clientX, clientY } = event;
            const deltaX = clientX - partMenuDragAnchorRef.current.mouseX;
            const deltaY = clientY - partMenuDragAnchorRef.current.mouseY;
            setPartMenuDelta({
                x: partMenuDragAnchorRef.current.dx + deltaX,
                y: partMenuDragAnchorRef.current.dy + deltaY,
            });
        };

        const handleMouseUp = () => setIsDraggingPartMenu(false);

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDraggingPartMenu]);

    const canNavigatePdf = Boolean(pdfUrl && numPages);

    const statusContent = (loading || error) && (
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                zIndex: 999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                background: (theme) =>
                    theme.palette.mode === "dark"
                        ? "rgba(0,0,0,0.85)"
                        : "rgba(255,255,255,0.95)",
            }}
        >
            {error ? (
                <Box
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        boxShadow: 3,
                        backgroundColor: "background.paper",
                        maxWidth: 420,
                        textAlign: "center",
                    }}
                >
                    <Typography color="error" variant="h6" mb={1}>
                        {error}
                    </Typography>
                    <Typography variant="body2">
                        Please refresh the page or go back.
                    </Typography>
                </Box>
            ) : (
                <LoadingBoxes
                    text={loadingMessages[loadingStep]}
                    subText="Note: The 2D drawing may take up to a minute to load."
                    percentage={Math.round(percentage)}
                />
            )}
        </Box>
    );

    return (
        <Box
            sx={{
                height: "100vh",
                width: "100vw",
                overflow: "hidden",
                position: "relative",
                backgroundColor: "#f7f7f7",
            }}
        >
            {statusContent}

            {!loading && !error && (
                <>
                    <Box
                        position="absolute"
                        top={16}
                        left="50%"
                        sx={{ transform: "translateX(-50%)", zIndex: 5 }}
                    >
                        <PageTitle
                            title={`${machineNumber || ""} | ${machineName || ""
                                }`}
                        />
                    </Box>

                    <Box
                        sx={{
                            position: "absolute",
                            top: 16,
                            left: 24,
                            zIndex: 6,
                            pointerEvents: "auto",
                        }}
                    >
                        <Tooltip
                            title={showMenuBox ? "Hide Panel" : "Show Panel"}
                        >
                            <Button
                                variant="modelMenuButtons"
                                onClick={() =>
                                    setShowMenuBox((previous) => !previous)
                                }
                            >
                                <MenuIcon />
                            </Button>
                        </Tooltip>
                    </Box>

                    <Box position="absolute" top={16} right={24} zIndex={5}>
                        <Tooltip title={t("UI_TEXT.TOOLTIP_CLOSE")}>
                            <IconButton onClick={handleClose}>
                                <Close />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Box
                        sx={{
                            position: "absolute",
                            top: 96,
                            bottom: 120,
                            left: 24,
                            right: 24,
                            display: "grid",
                            gridTemplateColumns: showMenuBox
                                ? "minmax(340px, 460px) minmax(0, 1fr)"
                                : "minmax(0, 1fr)",
                            columnGap: showMenuBox ? 2 : 0,
                            alignItems: "stretch",
                            pointerEvents: "auto",
                            transition: "grid-template-columns 0.3s ease",
                        }}
                    >
                        {showMenuBox && (
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    bgcolor: "rgba(255,255,255,0.95)",
                                    borderRadius: 2,
                                    boxShadow: 2,
                                    overflow: "hidden",
                                    pointerEvents: "auto",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Tabs
                                    value={tabValue}
                                    onChange={(_, value) => setTabValue(value)}
                                    variant="fullWidth"
                                    textColor="primary"
                                    indicatorColor="primary"
                                    sx={{ borderBottom: 1, borderColor: "divider", mb: 0.75 }}
                                >
                                    <Tab value={0} label={menuTabLabels.subAssemblies} />
                                    {rsplFeatureEnabled && <Tab value={1} label={menuTabLabels.rspl} />}
                                    <Tab value={2} label={menuTabLabels.partsTree} />
                                </Tabs>

                                {tabValue === 0 && (
                                    <TabPanel
                                        value={tabValue}
                                        index={0}
                                        title={menuTabLabels.subAssemblies}
                                        subtitle={showMenuTabSubtitles ? "Level 1 subassemblies from the assembly tree." : ""}
                                        headerActions={
                                            <Tooltip title="Scroll to Top">
                                                <IconButton
                                                    size="small"
                                                    onClick={scrollSubAssembliesToTop}
                                                >
                                                    <VerticalAlignTop fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    >
                                        <SearchInput
                                            value={subSearchTerm}
                                            onChange={(e) =>
                                                setSubSearchTerm(
                                                    e.target.value
                                                )
                                            }
                                            onSubmit={() =>
                                                setSubSearchTerm((prev) => prev)
                                            }
                                        />
                                        <Box
                                            ref={subAssembliesScrollRef}
                                            sx={{
                                                mt: 1,
                                                flex: 1,
                                                overflowY: "auto",
                                                pr: 1,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                            }}
                                        >
                                            {renderSubAssemblyCards.length >
                                                0 ? (
                                                renderSubAssemblyCards
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    textAlign="center"
                                                >
                                                    No sub-assemblies found.
                                                </Typography>
                                            )}
                                        </Box>
                                    </TabPanel>
                                )}

                                {rsplFeatureEnabled && tabValue === 1 && (
                                    <TabPanel
                                        value={tabValue}
                                        index={1}
                                        title={menuTabLabels.rspl}
                                        subtitle={showMenuTabSubtitles ? "List RSPL parts from products with their mapped types." : ""}
                                        headerActions={
                                            <>
                                                <Tooltip title="Scroll to Top">
                                                    <IconButton
                                                        size="small"
                                                        onClick={scrollRsplToTop}
                                                    >
                                                        <VerticalAlignTop fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        }
                                    >
                                        <SearchInput
                                            value={rsplSearchTerm}
                                            onChange={(e) =>
                                                setRsplSearchTerm(
                                                    e.target.value
                                                )
                                            }
                                            onSubmit={() =>
                                                setRsplSearchTerm((prev) => prev)
                                            }
                                        />
                                        <Tabs
                                            value={innerTab}
                                            onChange={(_, value) =>
                                                setInnerTab(value)
                                            }
                                            sx={{ borderBottom: 1, borderColor: "divider", mb: 0.75 }}
                                        >
                                            <Tab label="All" value="All" />
                                            {productTypes.map((type) => (
                                                <Tab
                                                    key={type}
                                                    label={type}
                                                    value={type}
                                                />
                                            ))}
                                        </Tabs>
                                        <Box
                                            ref={rsplScrollRef}
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 1,
                                                mb: 1,
                                            }}
                                        >
                                            {rsplTypeLabels.map((type) => (
                                                <Button
                                                    key={`rspl-add-all-${type}`}
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => handleAddAllProductType(type)}
                                                >
                                                    Add All {type} Parts
                                                </Button>
                                            ))}
                                        </Box>
                                        <Box
                                            ref={rsplScrollRef}
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 1,
                                                mb: 1,
                                            }}
                                        >
                                            {rsplTypeLabels.map((type) => (
                                                <Button
                                                    key={`rspl-add-all-${type}`}
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => handleAddAllProductType(type)}
                                                >
                                                    Add All {type} Parts
                                                </Button>
                                            ))}
                                        </Box>
                                        <Box
                                            ref={rsplScrollRef}
                                            sx={{
                                                flex: 1,
                                                overflowY: "auto",
                                                pr: 1,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                            }}
                                        >
                                            {rsplContent.length > 0 ? (
                                                rsplContent
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    textAlign="center"
                                                >
                                                    No RSPL parts found.
                                                </Typography>
                                            )}
                                        </Box>
                                    </TabPanel>
                                )}

                                {tabValue === 2 && (
                                    <TabPanel
                                        value={tabValue}
                                        index={2}
                                        title={menuTabLabels.partsTree}
                                        subtitle={showMenuTabSubtitles ? "Assembly tree derived from model." : ""}
                                        headerActions={
                                            <>
                                                <Tooltip title="Expand All">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                setExpandedNodes(
                                                                    allNodeIds
                                                                )
                                                            }
                                                        >
                                                            <FiChevronsDown size={18} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Collapse All">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                setExpandedNodes(
                                                                    treeData.map(
                                                                        (node) =>
                                                                            node.nodeId
                                                                    )
                                                                )
                                                            }
                                                        >
                                                            <FiChevronsUp size={18} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Scroll to Top">
                                                    <IconButton
                                                        size="small"
                                                        onClick={scrollPartsTreeToTop}
                                                    >
                                                        <VerticalAlignTop fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                                height: "100%",
                                            }}
                                        >
                                            <SearchInput
                                                value={treeSearchTerm}
                                                onChange={(e) =>
                                                    setTreeSearchTerm(
                                                        e.target.value
                                                    )
                                                }
                                                onSubmit={
                                                    handleTreeSearchSubmit
                                                }
                                                placeholder={`Search in ${menuTabLabels.partsTree}`}
                                            />
                                            {treeSearchTerm && (
                                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                    <Button
                                                        size="small"
                                                        onClick={handleTreeSearchClear}
                                                    >
                                                        Clear Search
                                                    </Button>
                                                </Box>
                                            )}
                                            <Box
                                                ref={partsTreeScrollRef}
                                                sx={{
                                                    flex: 1,
                                                    overflowY: "auto",
                                                    pr: 1,
                                                }}
                                            >
                                                {filteredTree?.length ? (
                                                    renderBomNodes(filteredTree)
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        textAlign="center"
                                                        mt={5}
                                                    >
                                                        {menuTabLabels.partsTree} not available.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TabPanel>
                                )}
                            </Box>
                        )}
                        <Box
                            sx={{
                                width: "100%",
                                minWidth: 0,
                                height: "100%",
                                backgroundColor: "#fff",
                                borderRadius: 2,
                                boxShadow: 3,
                                overflow: "hidden",
                                position: "relative",
                                pointerEvents: "auto",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {currentPdfLabel && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        bottom: 15,
                                        right: 15,
                                        zIndex: 6,
                                        backgroundColor: "rgba(0,0,0,0.55)",
                                        borderRadius: 1,
                                        px: 1.5,
                                        py: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="#fff"
                                        fontSize={"11px"}
                                    // fontWeight="bold"
                                    >
                                        Showing Drawing: {currentPdfLabel}
                                    </Typography>
                                </Box>
                            )}

                            {pdfLoading && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor:
                                            "rgba(255,255,255,0.7)",
                                        zIndex: 5,
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            )}

                            {pdfUrl ? (
                                <Box
                                    onWheel={handlePdfWheel}
                                    onMouseDown={handlePdfMouseDown}
                                    onMouseMove={handlePdfMouseMove}
                                    onMouseUp={stopPdfPan}
                                    onMouseLeave={stopPdfPan}
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        overflow: "hidden",
                                        cursor: isPanningPdf
                                            ? "grabbing"
                                            : "grab",
                                        userSelect: "none",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "flex-start",
                                            transform: `translate(${pdfPan.x}px, ${pdfPan.y}px) scale(${scale})`,
                                            transformOrigin: "top center",
                                            willChange: "transform",
                                            transition: isPanningPdf
                                                ? "none"
                                                : "transform 0.15s ease-out",
                                        }}
                                    >
                                        <Box sx={{ px: 4, py: 3 }}>
                                            <Document
                                                file={pdfUrl}
                                                onLoadSuccess={
                                                    handleDocumentLoadSuccess
                                                }
                                                onLoadError={
                                                    handleDocumentLoadError
                                                }
                                                loading={
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            height: "100%",
                                                        }}
                                                    >
                                                        <CircularProgress />
                                                    </Box>
                                                }
                                            >
                                                <Page
                                                    pageNumber={pageNumber}
                                                    scale={1}
                                                    renderAnnotationLayer={false}
                                                    renderTextLayer={false}
                                                />
                                            </Document>
                                        </Box>
                                    </Box>
                                </Box>
                            ) : pdfError ? (
                                <>
                                    <Box sx={{ minHeight: '50px', minWidth: '200px', background: '#ddd', borderRadius: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, flexDirection: 'column', rowGap: 1 }}>
                                        <Typography
                                            variant="body2"

                                        >
                                            {pdfError}
                                        </Typography>
                                        <Typography variant="body2" fontSize={"11px"} color="text.secondary">
                                            You can still add the selected Part to Quote or Request list accordingly.
                                        </Typography>
                                    </Box>
                                </>
                            ) : !pdfLoading ? (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Select a part to view the drawing.
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 10,
                            left: 10,
                            maxWidth: "100%",
                            zIndex: 10,
                            pointerEvents: "auto",
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
                            Disclaimer: "Some drawings may contain parts that may not appear on the machine 2D or 3D model"
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 25,
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 10,
                            pointerEvents: "auto",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: 2.5,
                                px: 3,
                                py: 1.5,
                            }}
                        >
                            <Tooltip title={t("UI_TEXT.TOOLTIP_HOME_RELOAD_DRAWING")}>
                                <Button variant="modelMenuButtons" onClick={handleHome}>
                                    <HomeIcon />
                                </Button>
                            </Tooltip>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Tooltip title={t("UI_TEXT.TOOLTIP_PREVIOUS_PAGE")}>
                                    <span>
                                        <Button
                                            variant="modelMenuButtons"
                                            onClick={handlePrevPage}
                                            disabled={!canNavigatePdf || pageNumber <= 1}
                                        >
                                            <ArrowBackIosNew fontSize="small" />
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t("UI_TEXT.TOOLTIP_NEXT_PAGE")}>
                                    <span>
                                        <Button
                                            variant="modelMenuButtons"
                                            onClick={handleNextPage}
                                            disabled={!canNavigatePdf || (numPages ? pageNumber >= numPages : false)}
                                        >
                                            <ArrowForwardIos fontSize="small" />
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Tooltip title={t("UI_TEXT.TOOLTIP_ZOOM_IN")}>
                                    <span>
                                        <Button
                                            variant="modelMenuButtons"
                                            onClick={handleZoomIn}
                                            disabled={!pdfUrl}
                                        >
                                            <ZoomIn />
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t("UI_TEXT.TOOLTIP_ZOOM_OUT")}>
                                    <span>
                                        <Button
                                            variant="modelMenuButtons"
                                            onClick={handleZoomOut}
                                            disabled={!pdfUrl}
                                        >
                                            <ZoomOut />
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t("UI_TEXT.TOOLTIP_RESET_ZOOM")}>
                                    <span>
                                        <Button
                                            variant="modelMenuButtons"
                                            onClick={handleResetZoom}
                                            disabled={!pdfUrl}
                                        >
                                            <RestartAlt />
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>

                            <Tooltip title={t("UI_TEXT.TOOLTIP_HELP")}>
                                <Button variant="modelMenuButtons" onClick={handleHelpClick}>
                                    <QuestionMark />
                                </Button>
                            </Tooltip>

                            {/* <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    minWidth: 80,
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    Page
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {numPages ? `${pageNumber} / ${numPages}` : "--"}
                                </Typography>
                            </Box> */}

                            {/* <Tooltip title="Show Cart / Request List">
                                <Badge
                                    badgeContent={cartItems.length}
                                    color="error"
                                    showZero
                                >
                                    <Button variant="modelMenuButtons" onClick={handleCartClick}>
                                        <ShoppingCart />
                                    </Button>
                                </Badge>
                            </Tooltip> */}
                        </Box>
                    </Box>

                    {cartOpen && (
                        <Box
                            sx={{
                                position: "absolute",
                                bottom: 85,
                                right: 35,
                                zIndex: 25,
                                height: "75vh",
                                width: 500,
                                display: "flex",
                                flexDirection: "column",
                                backgroundColor: "rgba(255,255,255,0.95)",
                                borderRadius: 2,
                                boxShadow: 3,
                                p: 2,
                                pointerEvents: "auto",
                            }}
                        >
                            <Typography fontWeight="bold">Quote</Typography>
                            <Typography variant="body2" color="text.secondary">
                                List of all Parts in Quote
                            </Typography>
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    overflowY: "auto",
                                    my: 1,
                                }}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <b>{t('LABELS.PART_NUMBER')}</b>
                                            </TableCell>
                                            <TableCell align="center">
                                                <b>Qty</b>
                                            </TableCell>
                                            <TableCell>
                                                <b>Price</b>
                                            </TableCell>
                                            <TableCell>
                                                <b>Action</b>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cartItems.map((item) => (
                                            <TableRow
                                                key={item.part_number}
                                            >
                                                <TableCell>
                                                    <Typography noWrap>
                                                        {item.part_number}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                decrementQty(
                                                                    item.part_number
                                                                )
                                                            }
                                                        >
                                                            −
                                                        </IconButton>
                                                        <Typography>
                                                            {item.qty}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                incrementQty(
                                                                    item.part_number
                                                                )
                                                            }
                                                        >
                                                            +
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {item.price ?? "N/A"}
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() =>
                                                            removeCartItem(
                                                                item.part_number
                                                            )
                                                        }
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                            <Typography fontWeight="bold">
                                Request List
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                List of all Parts in Request List
                            </Typography>
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    overflowY: "auto",
                                    my: 1,
                                }}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <b>{t('LABELS.PART_NUMBER')}</b>
                                            </TableCell>
                                            <TableCell>
                                                <b>Action</b>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {requestItems.map((item) => (
                                            <TableRow
                                                key={item.part_number}
                                            >
                                                <TableCell>
                                                    <Typography noWrap>
                                                        {item.part_number}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() =>
                                                            removeRequestItem(
                                                                item.part_number
                                                            )
                                                        }
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setCartOpen(false)}
                                    >
                                        {t("BUTTON.CLOSE")}
                                    </Button>
                                    <Button
                                        variant="text"
                                        color="warning"
                                        onClick={handleClearCart}
                                    >
                                        Clear Quote
                                    </Button>
                                    <Button
                                        variant="text"
                                        color="warning"
                                        onClick={handleClearRequestList}
                                    >
                                        Clear Request List
                                    </Button>
                                </Box>
                                <Button
                                    variant="contained"
                                    onClick={handleOrderItems}
                                    disabled={orderingItems}
                                >
                                    {orderingItems ? "Processing..." : "Quote Items"}
                                </Button>
                            </Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                mt={1}
                            >
                                <b>Note:</b> Quote & request list items here are
                                specific to this drawing session.
                            </Typography>
                        </Box>
                    )}

                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 25,
                            right: 35,
                            zIndex: 10,
                            pointerEvents: 'auto',
                        }}
                    >
                        <Badge
                            badgeContent={cartItems.length}
                            color="error"
                            showZero
                            overlap="circular"
                        >
                            <Box
                                onClick={handleCartClick}
                                sx={{
                                    width: 50,
                                    height: 50,
                                    bgcolor: (theme) =>
                                        theme.palette.primary.main,
                                    color: '#fff',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        bgcolor: (theme) =>
                                            theme.palette.primary.dark,
                                        transform: 'scale(1.05)',
                                    },
                                    '&:active': {
                                        transform: 'scale(0.95)',
                                    },
                                }}
                            >
                                <ShoppingCart />
                            </Box>
                        </Badge>
                    </Box>

                    {showPartMenu && selectedPart && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 90,
                                right: 30,
                                zIndex: 30,
                                transform: `translate(${partMenuDelta.x}px, ${partMenuDelta.y}px)`,
                                pointerEvents: "auto",
                            }}
                        >
                            <PartMenu
                                onHeaderMouseDown={handlePartMenuMouseDown}
                                data={selectedPart}
                                onClose={() => setShowPartMenu(false)}
                                isInCart={isInCart}
                                isInRequest={isInRequest}
                                addToCart={addToCart}
                                addToRequestList={addToRequestList}
                                enableModelActions={false}
                            />
                        </Box>
                    )}

                    <ModalWrapper
                        open={helpOpen}
                        setOpen={setHelpOpen}
                        onClose={() => setHelpOpen(false)}
                        title="2D Model Viewer Help"
                        subtitle="Quick reference for the icons and controls."
                        width="70vw"
                        content={helpContent}
                        // actions={
                        //     <>
                        //         <Button
                        //             variant="contained"
                        //             startIcon={<SupportAgent />}
                        //             onClick={handleContactCcrClick}
                        //         >
                        //             {t('LABELS.CONTACT_CCR')}
                        //         </Button>
                        //         <Button variant="contained" onClick={() => setHelpOpen(false)}>
                        //             {t("BUTTON.CLOSE")}
                        //         </Button>
                        //     </>
                        // }
                    />

                    <ContactCCRForm
                        open={ccrOpen}
                        setOpen={setCcrOpen}
                        machine={machine}
                        prefill={ccrPrefill}
                    />
                </>
            )}
        </Box>
    );
};

export default PDFModelViewer;
